import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ShoppingCart, Search, X, Loader2, RotateCw,
  Check, CornerUpLeft, Split, Merge, ArrowRight, Package, FileText, Store, Pencil, Receipt,
  PackageCheck
} from 'lucide-react'
import { apiGet, cachedGet, postJson } from '../lib/api.js'
import { useMe } from '../lib/me.js'
import { useEphemeralState } from '../lib/persist.js'
import { canManage } from '../lib/role.js'
import { useXanoRealtime } from '../lib/realtime.js'
import { rows } from '../lib/stock.js'
import { relabelBranch } from '../lib/ticket.js'
import {
  STAGES, STAGE_REQUEST, STAGE_INSPECT, STAGE_PAYMENT, STAGE_RECEIVE, STAGE_LOG,
  groupByPO, reqItemName, reqUnit, reqQty, poNumber, creatorCode,
  branchName, sellerName, lineGoods, fmtBaht, fmtDate, buildEditPayload,
  reqReceived, reqOutstanding, lineReceived, lineOverReceived,
} from '../lib/purchase.js'
import PurchaseCreateDrawer from '../components/purchase/PurchaseCreateDrawer.jsx'
import PurchaseEditDrawer from '../components/purchase/PurchaseEditDrawer.jsx'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import QtyStepper from '../components/purchase/QtyStepper.jsx'
import { TH_MONTHS_ABBR } from '../lib/expense.js'
import Pager from '../components/Pager.jsx'
import ExpenseEditor from '../components/expense/ExpenseEditor.jsx'

const PER_PAGE = 100

export default function Purchase() {
  const me = useMe()
  const isManager = canManage(me)
  const myEmpId = me?.employee_id || me?.xano_id || 0

  const [stage, setStage] = useEphemeralState('purchase:stage', STAGE_REQUEST)
  const [q, setQ] = useEphemeralState('purchase:q', '')
  const [branchId, setBranchId] = useEphemeralState('purchase:branch', '')
  const [page, setPage] = useState(1)

  const [branches, setBranches] = useState([])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  const [createOpen, setCreateOpen] = useState(false)
  const [editPo, setEditPo] = useState(null)        // PO key being edited (stage 2)
  const [editLine, setEditLine] = useState(0)       // line to highlight inside that drawer
  // The PO's paperwork/expense branch, per PO key. Xano has nowhere to store it
  // until the PO advances (it becomes procurement.branch_id → expense.branch_id),
  // so it lives here — shared by the card footer and the editor drawer, and read
  // by ส่งไปชำระเงิน. Unset = the requesting branch of the PO's first line.
  // The linked expense, opened in place rather than by navigating to ค่าใช้จ่าย —
  // checking what a PO booked should not cost you your spot in จัดซื้อ.
  const [expenseId, setExpenseId] = useState(0)
  const branchNameMap = useMemo(
    () => Object.fromEntries(rows(branches).map((b) => [String(b.id), b.name || b.Name || `สาขา #${b.id}`])),
    [branches])

  const [poBranch, setPoBranch] = useState({})
  const poBranchOf = (g) => poBranch[g.key] ?? String(g.procBranchId || g.branchId || '')
  const setPoBranchFor = (key, v) => setPoBranch((m) => ({ ...m, [key]: v }))
  const [selected, setSelected] = useState(() => new Set())
  const [acting, setActing] = useState(false)
  const [toast, setToast] = useState(null)

  const refresh = () => setReloadKey((k) => k + 1)

  useEffect(() => { cachedGet('/api/branches').then((r) => setBranches(rows(r))).catch(() => {}) }, [])

  // Reset page + selection whenever the view changes (a bulk action must never
  // span stages — each stage's actions are distinct).
  useEffect(() => { setPage(1); setSelected(new Set()) }, [stage, branchId])

  useEffect(() => {
    let alive = true
    setLoading(true)
    apiGet('/api/purchase', { stage, branch_id: branchId || undefined, page, per_page: PER_PAGE })
      .then((r) => { if (alive) { setData(r); setError(null) } })
      .catch((e) => { if (alive) setError(e.message || 'โหลดรายการจัดซื้อไม่สำเร็จ') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [stage, branchId, page, reloadKey])

  // Live refresh — the Xano purchase endpoints broadcast on the shared "SSDC"
  // channel. Debounced; a self-echo just costs one harmless refetch.
  const rtTimer = useRef(null)
  useXanoRealtime('SSDC', () => {
    clearTimeout(rtTimer.current)
    rtTimer.current = setTimeout(refresh, 600)
  })
  useEffect(() => () => clearTimeout(rtTimer.current), [])

  const items = useMemo(() => rows(data), [data])
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return items
    return items.filter((r) =>
      reqItemName(r).toLowerCase().includes(needle) ||
      poNumber(r).toLowerCase().includes(needle) ||
      sellerName(r).toLowerCase().includes(needle) ||
      String(branchName(r)).toLowerCase().includes(needle) ||
      String(r?.remark || '').toLowerCase().includes(needle))
  }, [items, q])

  const groups = useMemo(() => groupByPO(visible), [visible])
  // Re-read the open PO from the live groups so a realtime refresh keeps the
  // drawer's baseline current; if the PO vanishes (merged away, last line
  // deleted) the drawer closes itself rather than editing a stale row.
  const editGroup = useMemo(() => groups.find((g) => g.key === editPo) || null, [groups, editPo])
  const total = Number(data?.itemsTotal) || 0
  const pageTotal = Number(data?.pageTotal) || 1

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  // Whole-PO select — merging two POs together shouldn't mean ticking every line.
  const toggleGroup = (g) => setSelected((s) => {
    const n = new Set(s)
    const all = g.rows.every((r) => n.has(r.id))
    for (const r of g.rows) all ? n.delete(r.id) : n.add(r.id)
    return n
  })
  const clearSel = () => setSelected(new Set())

  async function run(fn, okMsg) {
    if (acting) return
    setActing(true); setError(null)
    try { await fn(); clearSel(); if (okMsg) setToast(okMsg); refresh() }
    catch (e) { setError(e.message || 'ดำเนินการไม่สำเร็จ') }
    finally { setActing(false) }
  }

  const approve = (ids) => run(() => postJson('/api/purchase/approve', { inventory_request_id: ids }), `อนุมัติ ${ids.length} รายการแล้ว — ออกเลข PO ให้อัตโนมัติ`)
  // Merge selected lines under one PO. Lines KEEP their own branch — that is the
  // branch that needs the item, and pooling several branches' requests into one
  // order is the point of merging. Which branch carries the PO's paperwork and
  // expense is a separate choice, made on the card when it goes to ชำระเงิน.
  const merge = (ids) => run(
    () => postJson('/api/purchase/merge', { request_id: ids }),
    `รวม ${ids.length} รายการเป็น PO เดียวแล้ว`)
  // Unmerge = give each selected line a fresh PO. The create/upsert endpoint
  // re-issues a new monthly PO when sent stage 2 + empty PO_number, so no
  // dedicated endpoint is needed — loop it, preserving each row's created_by.
  const unmerge = (ids) => run(async () => {
    const byId = new Map(items.map((r) => [r.id, r]))
    for (const id of ids) {
      const row = byId.get(id)
      if (!row) continue
      await postJson('/api/purchase', buildEditPayload(row, { stage: STAGE_INSPECT, PO_number: '' }))
    }
  }, 'แยกออกเป็น PO ใหม่แล้ว')
  const sendBack = (row) => run(
    () => postJson('/api/purchase', buildEditPayload(row, { approve: 'ไม่อนุมัติ', stage: STAGE_INSPECT })),
    'ส่งคำขอกลับไปแก้ไข (คำขอซื้อ) แล้ว')
  // Every PO-level stage move goes through /api/purchase/step → the app's own
  // Xano endpoint (api:FeVDPm_m/purchase/step). It moves the lines AND keeps the
  // PO-level `procurement` record in step: created at 2→3, deactivated at 3→2,
  // and at 3→4 it books the expense. seller_id is passed on every move because
  // the endpoint writes it onto each line — omitting it would clear a seller the
  // PO already has.
  const stepPO = (group, step, type, okMsg, extra = {}) => run(
    () => postJson('/api/purchase/step', {
      step, type, PO_number: group.po,
      request_id: group.rows.map((r) => r.id),
      seller_id: Number(group.sellerId) || 0,
      procurement_branch_id: Number(poBranchOf(group)) || group.branchId || 0,
      ...extra,
    }),
    okMsg)

  const advance = (group) => stepPO(group, STAGE_INSPECT, 'next', `ส่ง PO ${group.po} ไปขั้นชำระเงินแล้ว`)
  const backToInspect = (group) => stepPO(group, STAGE_PAYMENT, 'previous', `ส่ง PO ${group.po} กลับไปขั้นตรวจสอบแล้ว`)
  // 3 → 4. Xano builds the billing rows and the total from the lines themselves,
  // copies the PO's attachments onto the expense, and links it back through
  // procurement.expense_id — so confirming twice updates that one expense.
  const confirmPayment = (group, sellerId, period) => stepPO(
    group, STAGE_PAYMENT, 'next',
    `ยืนยันชำระเงิน PO ${group.po} แล้ว — บันทึกค่าใช้จ่ายและส่งไปขั้นรับของ`,
    {
      seller_id: Number(sellerId) || Number(group.sellerId) || 0,
      expense_year: Number(period?.year) || 0,
      expense_month_num: Number(period?.month) || 0,
    })
  // 4 → 3. Reopening a PO does NOT unbook its expense — Stage45 warns about that
  // before calling this.
  const backToPayment = (group) => stepPO(
    group, STAGE_RECEIVE, 'previous', `ส่ง PO ${group.po} กลับไปขั้นชำระเงินแล้ว`)
  // รับของ — one delivery against one line. Xano writes the stock row and, when
  // that was the last outstanding line, closes the whole PO into บันทึก.
  const receiveLine = (row, qty) => run(async () => {
    const r = await postJson('/api/purchase/receive', {
      inventory_request_id: row.id, N: Math.trunc(Number(qty) || 0),
    })
    setToast(r?.complete
      ? `รับครบทุกรายการ — ปิด PO ${poNumber(row)} ไปขั้นบันทึกแล้ว`
      : `รับ ${reqItemName(row)} ${qty} ${reqUnit(row)} แล้ว`)
  })
  const closePo = (group) => stepPO(
    group, STAGE_RECEIVE, 'next', `ปิด PO ${group.po} — ย้ายไปบันทึกแล้ว`)

  return (
    <div className="px-10 py-8 max-w-[1200px]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight mb-1">จัดซื้อ</h1>
          <p className="text-[14px] text-slate-500">คำขอซื้อ → ตรวจสอบ → ชำระเงิน → รับของ → บันทึก</p>
        </div>
        <button onClick={() => setCreateOpen(true)}
          className="shrink-0 rounded-xl bg-brand-600 px-5 py-2.5 text-[14.5px] font-semibold text-white hover:bg-brand-700 flex items-center gap-2">
          <ShoppingCart size={16} />สร้างคำขอซื้อ
        </button>
      </div>

      {/* Stage tabs — no-scrollbar: the row stays scrollable on a narrow window,
          but `-mb-px` on each tab overflows it by 1px and `overflow-x` forces
          overflow-y to auto, which drew a stub scrollbar beside the tabs. */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-4 overflow-x-auto no-scrollbar">
        {STAGES.map((s, i) => (
          <button key={s.stage} onClick={() => setStage(s.stage)}
            className={`relative px-4 py-2.5 text-[14px] font-medium -mb-px border-b-2 transition whitespace-nowrap ${
              stage === s.stage ? 'border-brand-500 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <span className="text-slate-300 mr-1.5 tabular-nums">{i + 1}</span>{s.label}
            {stage === s.stage && total > 0 && (
              <span className="ml-2 rounded-full bg-brand-100 text-brand-700 px-2 py-0.5 text-[11.5px] tabular-nums">{total}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-[380px]">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="กรองในหน้านี้… (ชื่อของ · PO · ผู้ขาย · หมายเหตุ)"
            className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-9 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 focus:border-brand-300 focus:ring-2 focus:ring-brand-100 outline-none" />
          {q && <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={15} /></button>}
        </div>
        <label className="flex items-center gap-2 text-[13px] text-slate-600">
          <span className="text-slate-400">สาขา</span>
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800">
            <option value="">ทุกสาขา</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name || b.Name) || `สาขา ${b.id}`}</option>)}
          </select>
        </label>
        <button onClick={refresh} title="โหลดใหม่" className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:text-brand-600 hover:border-brand-300">
          <RotateCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <p className="text-[12.5px] text-slate-400 mb-3">{stageMetaDesc(stage)}</p>

      {error && <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{error}</div>}

      {/* Manager gate note for stages 2 & 3 */}
      {(stage === STAGE_INSPECT || stage === STAGE_PAYMENT) && !isManager && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800">
          ขั้นตอนนี้จัดการได้เฉพาะผู้จัดการ / Stock Manager / Admin — คุณดูได้อย่างเดียว
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm px-5 py-16 text-center text-slate-400">
          <Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลด…
        </div>
      ) : !visible.length ? (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm px-5 py-16 text-center text-slate-400">
          {q ? `ไม่พบรายการที่ตรงกับ “${q}” ในหน้านี้` : 'ไม่มีรายการในขั้นตอนนี้'}
        </div>
      ) : stage === STAGE_REQUEST ? (
        <Stage1
          rows={visible} isManager={isManager} myEmpId={myEmpId} selected={selected}
          toggle={toggle} acting={acting} onApprove={approve} onClearSel={clearSel}
          onEdit={(r) => { setEditPo(poNumber(r) || `#${r.id}`); setEditLine(0) }}
        />
      ) : stage === STAGE_INSPECT ? (
        <Stage2
          groups={groups} isManager={isManager} selected={selected} toggle={toggle}
          acting={acting} onMerge={merge} onUnmerge={unmerge} onSendBack={sendBack}
          onAdvance={advance} onClearSel={clearSel} branches={branches} onToggleGroup={toggleGroup}
          poBranchOf={poBranchOf}
          onEdit={(g, lineId) => { setEditPo(g.key); setEditLine(lineId || 0) }}
        />
      ) : stage === STAGE_PAYMENT ? (
        <Stage3
          groups={groups} isManager={isManager} acting={acting} branches={branches}
          onEdit={(g, lineId) => { setEditPo(g.key); setEditLine(lineId || 0) }}
          onBack={backToInspect} onConfirm={confirmPayment} poBranchOf={poBranchOf}
          onOpenExpense={setExpenseId}
        />
      ) : (
        <ReadonlyGroups
          groups={groups} stage={stage} isManager={isManager} acting={acting}
          onBack={backToPayment} onClosePo={closePo} onReceive={receiveLine}
          onOpenExpense={setExpenseId}
          onEdit={(g, lineId) => { setEditPo(g.key); setEditLine(lineId || 0) }}
        />
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between gap-4 mt-4 text-[13px] text-slate-500">
        <div>{q ? <>แสดง <b className="text-slate-700">{visible.length}</b> จาก {items.length} ในหน้านี้ · ทั้งหมด {total}</> : <>ทั้งหมด <b className="text-slate-700">{total}</b> รายการ</>}</div>
        <Pager page={page} pageTotal={pageTotal} onChange={setPage} disabled={loading} />
      </div>

      {createOpen && (
        <PurchaseCreateDrawer
          branches={branches} defaultBranchId={me?.branch_id}
          onClose={() => setCreateOpen(false)}
          onSaved={(n) => { setCreateOpen(false); setToast(`สร้างคำขอซื้อ ${n} รายการแล้ว`); setStage(STAGE_REQUEST); setPage(1); refresh() }}
        />
      )}

      {editGroup && (
        <PurchaseEditDrawer
          group={editGroup} branches={branches} focusLineId={editLine}
          allowAdd={stage === STAGE_INSPECT || stage === STAGE_PAYMENT} canDelete={isManager}
          newLineStage={stage}
          showPrice={stage === STAGE_PAYMENT || stage === STAGE_RECEIVE}
          showSeller={stage === STAGE_PAYMENT || stage === STAGE_RECEIVE}
          showFiles={stage >= STAGE_INSPECT}
          readOnly={stage === STAGE_RECEIVE}
          poBranch={stage >= STAGE_INSPECT && stage <= STAGE_RECEIVE ? poBranchOf(editGroup) : null}
          onPoBranchChange={(v) => setPoBranchFor(editGroup.key, v)}
          title={stage === STAGE_REQUEST ? 'แก้ไขคำขอซื้อ' : undefined}
          subtitle={
            stage === STAGE_REQUEST ? 'แก้ไขก่อนผู้จัดการอนุมัติ'
            : stage === STAGE_PAYMENT ? 'ระบุราคาก่อนยืนยันการชำระเงิน'
            : stage === STAGE_RECEIVE ? 'ดูรายละเอียด · แนบไฟล์ได้' : undefined}
          onClose={() => setEditPo(null)}
          onSaved={(msg) => { setEditPo(null); setToast(msg); refresh() }}
          onDeleted={(msg) => { setEditPo(null); setToast(msg); refresh() }}
        />
      )}

      {expenseId > 0 && (
        <ExpenseEditor
          mode="edit" expenseId={expenseId} branchName={branchNameMap}
          onClose={() => setExpenseId(0)}
          onSaved={() => { setExpenseId(0); setToast('บันทึกใบค่าใช้จ่ายแล้ว'); refresh() }}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-xl bg-slate-900 text-white px-5 py-3 text-[14px] flex items-center gap-3 shadow-lg z-40">
          {toast}<button onClick={() => setToast(null)} className="text-slate-400 hover:text-white"><X size={15} /></button>
        </div>
      )}
    </div>
  )
}

function stageMetaDesc(stage) {
  return (STAGES.find((s) => s.stage === stage) || {}).desc || ''
}

// The PO's paperwork/expense branch, shown beside the PO number. Read-only here
// on purpose — it is a property of the PO, not of the row you happen to be
// looking at, so it is changed in one place: the editor drawer.
function PoBranchTag({ branchId, branches }) {
  const b = rows(branches).find((x) => Number(x.id) === Number(branchId))
  const name = relabelBranch(b?.name || b?.Name) || (branchId ? `สาขา ${branchId}` : '—')
  return (
    <span title="สาขาที่รับผิดชอบเอกสาร / ค่าใช้จ่ายของ PO นี้ (แก้ไขในหน้าต่างแก้ไข)"
      className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2 py-0.5 text-[11.5px] text-slate-600 whitespace-nowrap">
      <Receipt size={11} className="text-slate-400" />{name}
    </span>
  )
}

// Once a PO passes ชำระเงิน it owns an expense record, and going back a stage does
// not unbook it — so the card says so plainly, with the expense id to look up.
function ExpenseTag({ expenseId, onOpen }) {
  if (!(expenseId > 0)) return null
  const cls = 'inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11.5px] text-emerald-700 whitespace-nowrap'
  // The expense data itself is manager-gated (/api/expense*), so only offer the
  // drawer to someone who can load it; everyone else just sees that it is booked.
  if (!onOpen) {
    return <span title={`บันทึกค่าใช้จ่ายแล้ว — ใบที่ #${expenseId}`} className={cls}><Receipt size={11} />ค่าใช้จ่าย #{expenseId}</span>
  }
  return (
    <button onClick={() => onOpen(expenseId)}
      title={`บันทึกค่าใช้จ่ายแล้ว — ใบที่ #${expenseId} · คลิกเพื่อเปิดใบนี้`}
      className={`${cls} hover:bg-emerald-100 hover:border-emerald-300`}>
      <Receipt size={11} />ค่าใช้จ่าย #{expenseId}<Pencil size={10} className="opacity-60" />
    </button>
  )
}

/* ── Stage 1 — Request ─────────────────────────────────────────────────────── */
function Stage1({ rows: list, isManager, myEmpId, selected, toggle, acting, onApprove, onClearSel, onEdit }) {
  // A pending request is editable by whoever raised it (fix your own typo) and by
  // any manager. Deleting stays manager-only — the proxy enforces that too.
  const canEdit = (r) => isManager || Number(r?.created_by) === Number(myEmpId)
  const selectable = list.map((r) => r.id)
  const allSel = selectable.length > 0 && selectable.every((id) => selected.has(id))
  const selCount = selectable.filter((id) => selected.has(id)).length
  return (
    <>
      {isManager && (
        <div className="flex items-center gap-3 mb-3 min-h-[36px]">
          {selCount > 0 ? (
            <>
              <span className="text-[13px] text-slate-500">เลือก {selCount} รายการ</span>
              <button onClick={() => onApprove([...selected].filter((id) => selectable.includes(id)))} disabled={acting}
                className="rounded-lg bg-brand-600 px-4 py-1.5 text-[13.5px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 flex items-center gap-1.5">
                {acting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}อนุมัติที่เลือก
              </button>
              <button onClick={onClearSel} className="text-[13px] text-slate-400 hover:text-slate-600">ล้างการเลือก</button>
            </>
          ) : (
            <span className="text-[12.5px] text-slate-400">เลือกคำขอเพื่ออนุมัติ (อนุมัติแล้วจะออกเลข PO และส่งไปขั้นตรวจสอบ) · คลิกที่แถวเพื่อแก้ไข</span>
          )}
        </div>
      )}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] font-medium uppercase tracking-wide text-slate-400 border-b border-slate-100">
                {isManager && <th className="pl-5 pr-2 py-3 w-8"><input type="checkbox" checked={allSel} onChange={() => allSel ? onClearSel() : selectable.forEach((id) => !selected.has(id) && toggle(id))} className="accent-brand-600" /></th>}
                <th className="px-4 py-3 font-medium">วันที่</th>
                <th className="px-4 py-3 font-medium min-w-[220px]">ของ</th>
                <th className="px-4 py-3 font-medium">สาขา</th>
                <th className="px-4 py-3 font-medium text-right">จำนวน</th>
                <th className="px-4 py-3 font-medium">ผู้ขอ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((r) => (
                <tr key={r.id} onClick={() => canEdit(r) && onEdit?.(r)}
                  title={canEdit(r) ? 'คลิกเพื่อแก้ไข / ลบคำขอนี้' : undefined}
                  className={`hover:bg-slate-50/60 ${canEdit(r) ? 'cursor-pointer' : ''} ${selected.has(r.id) ? 'bg-brand-50/40' : ''}`}>
                  {isManager && (
                    <td className="pl-5 pr-2 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="accent-brand-600" />
                    </td>
                  )}
                  <td className="px-4 py-3 align-top whitespace-nowrap text-slate-500 tabular-nums">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3 align-top">
                    <div className="text-slate-800 flex items-center gap-1.5">
                      {reqItemName(r)}
                      {canEdit(r) && <Pencil size={11} className="text-slate-300 shrink-0" />}
                    </div>
                    {reqUnit(r) && <div className="text-[11.5px] text-slate-400">หน่วย: {reqUnit(r)}</div>}
                    {r?.remark && <div className="text-[11.5px] text-slate-400 italic mt-0.5">“{r.remark}”</div>}
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap text-slate-600">{relabelBranch(branchName(r))}</td>
                  <td className="px-4 py-3 align-top text-right tabular-nums font-semibold text-slate-800">{reqQty(r)}</td>
                  <td className="px-4 py-3 align-top whitespace-nowrap text-slate-500">{creatorCode(r) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

/* ── Stage 2 — Inspection ──────────────────────────────────────────────────── */
function Stage2({ groups, isManager, selected, toggle, acting, onMerge, onUnmerge, onSendBack, onAdvance, branches, onEdit, onToggleGroup, poBranchOf }) {
  const selCount = selected.size

  // Map each selected id back to its row + PO group.
  const byId = useMemo(() => {
    const m = new Map()
    for (const g of groups) for (const r of g.rows) m.set(r.id, { row: r, group: g })
    return m
  }, [groups])
  const selEntries = [...selected].map((id) => byId.get(id)).filter(Boolean)
  const crossBranch = new Set(selEntries.map((e) => Number(e.row.branch_id))).size > 1
  // แยกออก only means something when a selected line SHARES its PO with others.
  // If every selected line is already alone in its PO, there is nothing to split.
  const canUnmerge = selEntries.some((e) => e.group.lineCount > 1)

  return (
    <div className="space-y-3">
      {isManager && selCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-200 bg-brand-50/60 px-4 py-2.5">
          <span className="text-[13px] text-slate-600">เลือก {selCount} รายการ</span>
          {crossBranch && (
            <span className="text-[12.5px] text-slate-500">
              หลายสาขา — แต่ละรายการเก็บสาขาที่ขอไว้เหมือนเดิม เลือกสาขาที่รับผิดชอบเอกสารได้ตอนส่งไปชำระเงิน
            </span>
          )}
          <button onClick={() => onMerge([...selected])} disabled={acting || selCount < 2}
            className="rounded-lg bg-brand-600 px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 flex items-center gap-1.5">
            <Merge size={14} />รวมเป็น PO เดียว
          </button>
          <button onClick={() => onUnmerge([...selected])} disabled={acting || !canUnmerge}
            title={canUnmerge ? 'แยกแต่ละรายการออกเป็น PO ของตัวเอง' : 'รายการที่เลือกเป็น PO เดี่ยวอยู่แล้ว'}
            className="rounded-lg border border-slate-200 bg-white px-3.5 py-1.5 text-[13px] font-medium text-slate-600 hover:border-brand-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
            <Split size={14} />แยกออก
          </button>
        </div>
      )}
      {groups.map((g) => (
        <Stage2Card key={g.key} group={g} isManager={isManager} selected={selected} toggle={toggle}
          acting={acting} onSendBack={onSendBack} onAdvance={onAdvance} onEdit={onEdit}
          branches={branches} onToggleGroup={onToggleGroup} poBranchOf={poBranchOf} />
      ))}
    </div>
  )
}

function Stage2Card({ group, isManager, selected, toggle, acting, onSendBack, onAdvance, onEdit, branches, onToggleGroup, poBranchOf }) {
  const allSel = group.rows.length > 0 && group.rows.every((r) => selected.has(r.id))
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-3 bg-slate-50/70 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          {isManager && (
            <input type="checkbox" checked={allSel} onChange={() => onToggleGroup?.(group)}
              title="เลือกทั้ง PO (สำหรับรวม PO เข้าด้วยกัน)" className="accent-brand-600" />
          )}
          <FileText size={16} className="text-indigo-500" />
          {isManager ? (
            <button onClick={() => onEdit?.(group)} title="แก้ไข PO นี้"
              className="group/po font-semibold text-slate-800 tabular-nums rounded-lg px-1.5 -mx-1.5 py-0.5 hover:bg-indigo-50 hover:text-indigo-700 flex items-center gap-1.5">
              {group.po || '(ยังไม่มี PO)'}
              <Pencil size={12} className="text-slate-300 group-hover/po:text-indigo-500" />
            </button>
          ) : (
            <span className="font-semibold text-slate-800 tabular-nums">{group.po || '(ยังไม่มี PO)'}</span>
          )}
          <PoBranchTag branchId={poBranchOf(group)} branches={branches} />
          <span className="text-[12.5px] text-slate-400">· {group.lineCount} รายการ · รวม {group.qty} ชิ้น</span>
        </div>
        {isManager && (
          <button onClick={() => onAdvance(group)} disabled={acting}
            className="rounded-lg bg-brand-600 px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 flex items-center gap-1.5">
            ส่งไปชำระเงิน<ArrowRight size={14} />
          </button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-slate-400 border-b border-slate-100">
              {isManager && <th className="pl-5 pr-2 py-2 w-8" />}
              <th className="px-3 py-2 font-medium min-w-[180px]">ของ</th>
              <th className="px-3 py-2 font-medium">สาขาที่ขอ</th>
              <th className="px-3 py-2 font-medium text-right whitespace-nowrap">จำนวน</th>
              {isManager && <th className="px-3 pr-5 py-2 font-medium text-right">จัดการ</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {group.rows.map((r) => (
              <tr key={r.id} className={selected.has(r.id) ? 'bg-brand-50/40' : ''}>
                {isManager && (
                  <td className="pl-5 pr-2 py-2.5">
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="accent-brand-600" />
                  </td>
                )}
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Package size={14} className="text-slate-300 shrink-0" />
                    <span className="text-slate-800">{reqItemName(r)}</span>
                  </div>
                  {r?.remark && <div className="mt-0.5 ml-6 text-[11.5px] text-slate-400 italic truncate">“{r.remark}”</div>}
                </td>
                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{relabelBranch(branchName(r))}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{reqQty(r)} {reqUnit(r)}</td>
                {isManager && (
                  <td className="px-3 pr-5 py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => onEdit?.(group, r.id)} disabled={acting} title="แก้ไขรายการนี้ (จำนวน · สาขาที่ขอ · หมายเหตุ)"
                        className="rounded-lg border border-slate-200 px-2 py-1 text-[12px] text-slate-500 hover:text-indigo-700 hover:border-indigo-200 disabled:opacity-40 flex items-center gap-1">
                        <Pencil size={13} />แก้ไข
                      </button>
                      <button onClick={() => onSendBack(r)} disabled={acting} title="ส่งกลับไปแก้ไข (คำขอซื้อ)"
                        className="rounded-lg border border-slate-200 px-2 py-1 text-[12px] text-slate-500 hover:text-amber-700 hover:border-amber-200 disabled:opacity-40 flex items-center gap-1">
                        <CornerUpLeft size={13} />ส่งกลับ
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isManager && (
        <div className="px-5 py-2.5 border-t border-slate-100 text-[12px] text-slate-400">
          ป้ายสาขาบนหัว PO = สาขาที่รับผิดชอบเอกสาร/ค่าใช้จ่าย · สาขาบนแต่ละรายการ = สาขาที่ขอของ · แก้ทั้งสองอย่างได้โดยคลิกเลข PO · เลือกผู้ขายในขั้นชำระเงิน
        </div>
      )}
    </div>
  )
}

/* ── Stage 3 — Payment ─────────────────────────────────────────────────────────
 * Same card shape as ตรวจสอบ, plus the two things this stage exists for: a seller
 * and the money. Confirming here is the point of no return — Xano creates the
 * `procurement` record AND the expense — so ผู้ขาย is required before ยืนยัน, and
 * going back to ตรวจสอบ stays available until then.
 */
function Stage3({ groups, isManager, acting, branches, onEdit, onBack, onConfirm, poBranchOf, onOpenExpense }) {
  const [confirmPo, setConfirmPo] = useState(null)   // the PO awaiting its confirm dialog
  return (
    <div className="space-y-3">
      {confirmPo && (
        <ConfirmPaymentDialog
          group={confirmPo}
          onCancel={() => setConfirmPo(null)}
          onConfirm={(period) => { const g = confirmPo; setConfirmPo(null); onConfirm(g, g.sellerId, period) }}
        />
      )}
      {groups.map((g) => {
        const picked = g.sellerId ? { id: g.sellerId, name: g.sellerName, code: g.sellerCode } : null
        const priced = g.rows.every((r) => lineGoods(r) != null)
        return (
          <div key={g.key} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-3 bg-slate-50/70 border-b border-slate-100">
              <div className="flex items-center gap-2.5 min-w-0">
                <FileText size={16} className="text-violet-500" />
                {isManager ? (
                  <button onClick={() => onEdit?.(g)} title="แก้ไข PO นี้ (ราคา · จำนวน · สาขาที่ขอ)"
                    className="group/po font-semibold text-slate-800 tabular-nums rounded-lg px-1.5 -mx-1.5 py-0.5 hover:bg-violet-50 hover:text-violet-700 flex items-center gap-1.5">
                    {g.po || '—'}<Pencil size={12} className="text-slate-300 group-hover/po:text-violet-500" />
                  </button>
                ) : (
                  <span className="font-semibold text-slate-800 tabular-nums">{g.po || '—'}</span>
                )}
                <PoBranchTag branchId={poBranchOf(g)} branches={branches} />
                <ExpenseTag expenseId={g.expenseId} onOpen={isManager ? onOpenExpense : null} />
                {/* ผู้ขาย sits in the header with the other PO-level facts (branch,
                    line count) rather than on its own row below the items — it
                    describes the whole PO, not any one line. Unset is a live
                    button, because it is the one thing blocking ยืนยัน. */}
                {picked ? (
                  <span title={picked.name}
                    className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 border border-violet-200 px-2.5 py-0.5 text-[12px] text-violet-800 max-w-[240px]">
                    <Store size={12} className="shrink-0" />
                    <span className="truncate">{picked.name}</span>
                  </span>
                ) : isManager ? (
                  <button onClick={() => onEdit?.(g)} title="เลือกผู้ขาย (เปิดหน้าต่างแก้ไข PO)"
                    className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-amber-300 bg-amber-50 px-2.5 py-0.5 text-[12px] font-medium text-amber-700 hover:border-amber-400 hover:bg-amber-100 whitespace-nowrap">
                    <Store size={12} />เลือกผู้ขาย
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-200 px-2.5 py-0.5 text-[12px] text-slate-400 whitespace-nowrap">
                    <Store size={12} />ยังไม่ได้เลือกผู้ขาย
                  </span>
                )}
                <span className="text-[12.5px] text-slate-400 truncate whitespace-nowrap">· {g.lineCount} รายการ</span>
              </div>
              <span className="text-[13.5px] text-slate-700 tabular-nums whitespace-nowrap">
                รวม <b>฿{fmtBaht(g.goodsTotal ?? 0)}</b>
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="pl-5 pr-3 py-2 font-medium min-w-[180px]">ของ</th>
                    <th className="px-3 py-2 font-medium">สาขาที่ขอ</th>
                    <th className="px-3 py-2 font-medium text-right whitespace-nowrap">จำนวน</th>
                    <th className="px-3 pr-5 py-2 font-medium text-right">ราคารวม</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {g.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="pl-5 pr-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <Package size={14} className="text-slate-300 shrink-0" />
                          <span className="text-slate-800">{reqItemName(r)}</span>
                        </div>
                        {r?.remark && <div className="mt-0.5 ml-6 text-[11.5px] text-slate-400 italic truncate">“{r.remark}”</div>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{relabelBranch(branchName(r))}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{reqQty(r)} {reqUnit(r)}</td>
                      <td className={`px-3 pr-5 py-2.5 text-right tabular-nums whitespace-nowrap ${
                        lineGoods(r) == null ? 'text-amber-600' : 'text-slate-700'}`}>
                        {lineGoods(r) == null ? 'ยังไม่ระบุราคา' : `฿${fmtBaht(lineGoods(r))}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isManager && (
              <div className="border-t border-slate-100 px-5 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[12px] text-slate-400">
                    {!picked ? 'เลือกผู้ขายในหน้าต่างแก้ไข (คลิกเลข PO) ก่อนจึงจะยืนยันได้'
                      : !priced ? 'บางรายการยังไม่ระบุราคา — คลิกเลข PO เพื่อใส่ราคา'
                      : 'ยืนยันแล้วระบบจะสร้างใบจัดซื้อและบันทึกค่าใช้จ่ายให้อัตโนมัติ'}
                  </span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => onBack(g)} disabled={acting}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] text-slate-600 hover:border-amber-300 hover:text-amber-700 disabled:opacity-40 flex items-center gap-1.5">
                      <CornerUpLeft size={13} />กลับไปตรวจสอบ
                    </button>
                    <button onClick={() => setConfirmPo(g)} disabled={acting || !picked || !priced}
                      title={!picked ? 'ต้องเลือกผู้ขายก่อน' : (!priced ? 'ทุกรายการต้องระบุราคาก่อน' : '')}
                      className="rounded-lg bg-brand-600 px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                      ยืนยันชำระเงิน<ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/* Confirming payment writes an accounting record, so it asks which period that
 * record belongs to. Defaults to this month — the common case — but a PO paid in
 * the first days of a month often belongs to the previous one, and correcting it
 * afterwards means editing the expense by hand. */
function ConfirmPaymentDialog({ group, onCancel, onConfirm }) {
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const years = [now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1]

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onCancel?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onCancel])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] animate-ss-pop-in" onMouseDown={onCancel} />
      <div className="relative w-full max-w-[420px] bg-white rounded-2xl shadow-2xl shadow-slate-900/20 ring-1 ring-slate-200 p-5 animate-ss-pop-in">
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full flex items-center justify-center ring-1 flex-none text-brand-600 bg-brand-50 ring-brand-100">
            <Receipt size={18} />
          </span>
          <div className="min-w-0">
            <div className="text-[15.5px] font-semibold text-slate-900">ยืนยันการชำระเงิน {group.po}</div>
            <div className="text-[13.5px] text-slate-600 mt-0.5">
              ระบบจะบันทึกค่าใช้จ่าย <b className="tabular-nums">฿{fmtBaht(group.goodsTotal ?? 0)}</b>
              {group.sellerName ? <> จาก {group.sellerName}</> : null} และส่ง PO ไปขั้นรับของ
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[12.5px] font-medium text-slate-500">เดือนที่ลงบัญชี</span>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800">
              {TH_MONTHS_ABBR.map((label, i) => <option key={label} value={i + 1}>{label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[12.5px] font-medium text-slate-500">ปี</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 tabular-nums">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
        </div>
        <p className="mt-2 text-[11.5px] text-slate-400">แก้ไขรายละเอียดอื่นได้ภายหลังที่หน้าค่าใช้จ่าย</p>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button type="button" onClick={onCancel}
            className="px-3 py-1.5 rounded-lg text-[14px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
          <button type="button" onClick={() => onConfirm({ year, month })}
            className="px-3.5 py-1.5 rounded-lg text-[14px] font-semibold bg-brand-600 hover:bg-brand-700 text-white flex items-center gap-1.5">
            ยืนยันชำระเงิน<ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}

/* One line in รับของ. Deliveries arrive in pieces, so the row tracks รับแล้ว x/N
 * and offers a stepper defaulting to what is still outstanding — the common case
 * is "the rest of it turned up", one click.
 *
 * A finished line collapses to a tick but keeps a รับเพิ่ม escape hatch: over-
 * receipt is allowed (the shelf really does have 12), it is just flagged amber so
 * nobody mistakes it for the ordered figure.
 */
function ReceiveRow({ row, acting, onReceive }) {
  const ordered = reqQty(row)
  const got = reqReceived(row)
  const outstanding = reqOutstanding(row)
  const done = lineReceived(row)
  const over = lineOverReceived(row)
  const [qty, setQty] = useState(String(outstanding || 1))

  // Follow the outstanding amount as receipts land.
  useEffect(() => { setQty(String(outstanding || 1)) }, [outstanding])

  return (
    <tr className={done ? 'bg-emerald-50/30' : ''}>
      <td className="pl-5 pr-3 py-2.5">
        <div className="flex items-center gap-2">
          {done
            ? <PackageCheck size={14} className="text-emerald-500 shrink-0" />
            : <Package size={14} className="text-slate-300 shrink-0" />}
          <span className="text-slate-800">{reqItemName(row)}</span>
        </div>
        {row?.remark && <div className="mt-0.5 ml-6 text-[11.5px] text-slate-400 italic truncate">“{row.remark}”</div>}
      </td>
      <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{relabelBranch(branchName(row))}</td>
      <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${
        over ? 'text-amber-600 font-medium' : done ? 'text-emerald-700' : 'text-slate-700'}`}>
        {got}/{ordered} {reqUnit(row)}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums text-slate-500 whitespace-nowrap">
        {lineGoods(row) == null ? '—' : `฿${fmtBaht(lineGoods(row))}`}
      </td>
      <td className="px-3 pr-5 py-2.5">
        {/* A line that is in cannot be received again — the count is settled, and
            any genuine extra belongs in ปรับ Stock with its own reason. The
            stepper is capped at what is still outstanding for the same reason. */}
        {done ? (
          <div className="flex items-center justify-end gap-1.5 text-[12px] text-emerald-700 whitespace-nowrap">
            <Check size={13} />รับครบแล้ว
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <QtyStepper value={qty} onChange={setQty} max={outstanding} />
            <button onClick={() => onReceive?.(row, qty)}
              disabled={acting || !(Math.trunc(Number(qty) || 0) > 0)}
              className="rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-[12.5px] font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-40 flex items-center gap-1.5 whitespace-nowrap">
              <PackageCheck size={13} />รับของ
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

/* ── Stages 4–5 read-only grouped view ─────────────────────────────────────── */
function ReadonlyGroups({ groups, stage, isManager, acting, onBack, onClosePo, onEdit, onReceive, onOpenExpense }) {
  const [backPo, setBackPo] = useState(null)
  const [closePo, setClosePo] = useState(null)
  // รับของ is the last stage a PO can still be worked on; บันทึก is history.
  const canAct = isManager && stage === STAGE_RECEIVE
  // Recording a delivery is open to every employee — whoever unpacks the box.
  const canReceive = stage === STAGE_RECEIVE
  return (
    <div className="space-y-3">
      {backPo && (
        <ConfirmDialog
          tone="danger"
          title={`ส่ง ${backPo.po} กลับไปขั้นชำระเงิน?`}
          confirmLabel="กลับไปชำระเงิน"
          onCancel={() => setBackPo(null)}
          onConfirm={() => { const g = backPo; setBackPo(null); onBack?.(g) }}
          message={
            <>
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[13px] text-amber-900">
                PO นี้บันทึกใบค่าใช้จ่ายไว้แล้ว — การย้ายกลับ <b>จะไม่ลบใบค่าใช้จ่ายนั้น</b>
              </div>
              <div className="mt-2 text-[13px] text-slate-600">
                ใบค่าใช้จ่ายยังอยู่ในระบบตามเดิม ถ้าต้องการยกเลิกต้องไปลบเองที่หน้าค่าใช้จ่าย
                · ยืนยันชำระเงินอีกครั้งจะอัปเดตใบเดิม ไม่สร้างใบใหม่
              </div>
            </>
          }
        />
      )}
      {closePo && (
        <ConfirmDialog
          title={`ปิด ${closePo.po} ไปขั้นบันทึก?`}
          confirmLabel="ปิด PO"
          onCancel={() => setClosePo(null)}
          onConfirm={() => { const g = closePo; setClosePo(null); onClosePo?.(g) }}
          message={
            <>
              <div>ย้าย {closePo.lineCount} รายการไปขั้นบันทึก (ประวัติ อ่านอย่างเดียว)</div>
              {closePo.outstandingQty > 0 ? (
                <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[13px] text-amber-900">
                  ยังรับไม่ครบ — ระบบจะบันทึกส่วนที่เหลืออีก <b>{closePo.outstandingQty} ชิ้น</b> เข้า
                  <b> ตัด Stock</b> ให้อัตโนมัติ (ประเภท “รับเพิ่ม”)
                  <div className="mt-1 text-amber-800/80">ถ้าของยังไม่มาจริง อย่าเพิ่งปิด PO</div>
                </div>
              ) : (
                <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[13px] text-emerald-900">
                  รับครบทุกรายการแล้ว — ไม่มีการบันทึกเข้า ตัด Stock เพิ่ม
                </div>
              )}
              <div className="mt-2 text-[13px] text-slate-500">
                ปกติ PO จะไปขั้นบันทึกเองเมื่อรับของครบทุกรายการ — ตอนนี้ยังไม่มีหน้าบันทึกรับของ จึงปิดด้วยมือไปก่อน
                · ย้ายกลับจากบันทึกยังทำไม่ได้
              </div>
            </>
          }
        />
      )}
      {groups.map((g) => (
        <div key={g.key} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-5 py-3 bg-slate-50/70 border-b border-slate-100">
            <div className="flex items-center gap-2.5 min-w-0">
              <FileText size={16} className="text-slate-400" />
              {canAct ? (
                <button onClick={() => onEdit?.(g)} title="ดูรายละเอียด · แนบไฟล์"
                  className="group/po font-semibold text-slate-800 tabular-nums rounded-lg px-1.5 -mx-1.5 py-0.5 hover:bg-sky-50 hover:text-sky-700 flex items-center gap-1.5">
                  {g.po || '—'}<Pencil size={12} className="text-slate-300 group-hover/po:text-sky-500" />
                </button>
              ) : (
                <span className="font-semibold text-slate-800 tabular-nums">{g.po || '—'}</span>
              )}
              <span className="text-[12.5px] text-slate-400 truncate">
                · {g.multiBranch ? `${g.branchIds.length} สาขา` : relabelBranch(g.branchName)} · {g.lineCount} รายการ
              </span>
              {canReceive && (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] whitespace-nowrap ${
                  g.allReceived ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700'}`}>
                  <PackageCheck size={12} />รับแล้ว {g.receivedLines}/{g.lineCount} รายการ
                </span>
              )}
              <ExpenseTag expenseId={g.expenseId} onOpen={isManager ? onOpenExpense : null} />
              {g.sellerName && <span className="inline-flex items-center gap-1 text-[12.5px] text-slate-500"><Store size={13} />{g.sellerName}</span>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {canAct && (
                <button onClick={() => setBackPo(g)} disabled={acting}
                  title="ส่งกลับไปแก้ไขที่ขั้นชำระเงิน"
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[12px] text-slate-500 hover:text-amber-700 hover:border-amber-300 disabled:opacity-40 flex items-center gap-1">
                  <CornerUpLeft size={12} />กลับไปชำระเงิน
                </button>
              )}
              {canAct && (
                <button onClick={() => setClosePo(g)} disabled={acting}
                  title="ปิด PO นี้ — ย้ายไปขั้นบันทึก"
                  className="rounded-lg bg-brand-600 px-2.5 py-1 text-[12px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 flex items-center gap-1">
                  ปิด PO<ArrowRight size={12} />
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="pl-5 pr-3 py-2 font-medium min-w-[180px]">ของ</th>
                  <th className="px-3 py-2 font-medium">สาขา</th>
                  <th className="px-3 py-2 font-medium text-right whitespace-nowrap">{canReceive ? 'รับแล้ว' : 'จำนวน'}</th>
                  <th className="px-3 py-2 font-medium text-right">ราคา</th>
                  {canReceive && <th className="px-3 pr-5 py-2 font-medium text-right">รับเข้า</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {g.rows.map((r) => (
                  canReceive
                    ? <ReceiveRow key={r.id} row={r} acting={acting} onReceive={onReceive} />
                    : (
                      <tr key={r.id}>
                        <td className="pl-5 pr-3 py-2.5 text-slate-800">{reqItemName(r)}</td>
                        <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{relabelBranch(branchName(r))}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{reqQty(r)} {reqUnit(r)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-slate-500 whitespace-nowrap">{lineGoods(r) == null ? '—' : `฿${fmtBaht(lineGoods(r))}`}</td>
                      </tr>
                    )
                ))}
              </tbody>
            </table>
          </div>
          {g.goodsTotal != null && (
            <div className="px-5 py-2.5 border-t border-slate-100 text-right text-[13px] text-slate-600">รวมมูลค่า: <b className="tabular-nums">฿{fmtBaht(g.goodsTotal)}</b></div>
          )}
        </div>
      ))}
      <p className="text-[12.5px] text-slate-400 px-1">
        {stage === STAGE_RECEIVE && 'รับของได้ทีละรายการ (รับบางส่วนได้) — เมื่อรับครบทุกรายการ PO จะย้ายไปขั้นบันทึกอัตโนมัติ'}
        {stage === STAGE_LOG && 'บันทึกการจัดซื้อที่เสร็จสิ้น — อ่านอย่างเดียว'}
      </p>
    </div>
  )
}
